import type {Scene} from '@babylonjs/core/scene';
import {Mesh} from '@babylonjs/core/Meshes/mesh';
import {CreatePlane} from '@babylonjs/core/Meshes/Builders/planeBuilder';
import {CreateGround} from '@babylonjs/core/Meshes/Builders/groundBuilder';
import {StandardMaterial} from '@babylonjs/core/Materials/standardMaterial';
import {Texture} from '@babylonjs/core/Materials/Textures/texture';
import {DynamicTexture} from '@babylonjs/core/Materials/Textures/dynamicTexture';
import {Color3} from '@babylonjs/core/Maths/math.color';
import {TERRAIN,type TerrainDamage} from '@arena/shared';

interface Soul {mesh:Mesh;x:number;y:number;z:number;phase:number;fright:number;}

/** Bounded scenery animation: shared textures, no lights or physical debris. */
export class InfernoDetails {
  private readonly textures=new Map<string,Texture>();
  private readonly souls:Soul[]=[];
  private readonly lanterns:{mesh:Mesh;phase:number}[]=[];
  private readonly mist:Mesh[]=[];
  private time=0;
  private path:Mesh|null=null;
  private pathBase:number[]=[];
  private pathKey='';
  private readonly scene:Scene;
  constructor(scene:Scene){
    this.scene=scene;
    // The dock and the queue extend beyond the fighting floor.
    for(const [x,z,height]of [[4.4,11.3,2.8],[7.1,14,3],[3.4,9.8,2.6],[-15,1.5,2.5]] as const){
      const mesh=this.sprite('lantern',x,0,z,height);
      this.lanterns.push({mesh,phase:x+z});
    }
    for(const [x,z,s]of [[-13.5,-8.2,1.5],[-8.4,-9.2,1.2],[-15.5,6,1.3],[-9.8,-9.6,1.4]]as const)this.sprite('graves',x,0,z,s);
    for(const [x,z,s]of [[-15.2,-4.8,1.7],[-14.7,4.9,1.5],[3.7,11.7,1.4],[7.8,12.5,1.5]]as const)this.sprite('chain',x,-.1,z,s);
    for(let i=0;i<7;i++){
      const x=-8+i*3,z=12.4+Math.sin(i*1.7)*.55;
      const mesh=this.sprite('souls',x,-.35,z,1.65+(i%3)*.13,.42);
      this.souls.push({mesh,x,y:mesh.position.y,z,phase:i*2.3,fright:0});
    }
    // Worn flagstones connect the Gate to the dock. They are paint on the floor.
    const paving=this.paving();
    // Leave the Gate, cross the player's starting lane, then turn north along
    // the east side of Charon's house to meet the centre of the pier.
    const route=[[-16.1,0],[-14.3,1.2],[5.75,1.2],[5.75,10.4]] as const;
    const stones:Mesh[]=[];
    for(let segment=1;segment<route.length;segment++){
      const [ax,az]=route[segment-1]!,[bx,bz]=route[segment]!;
      const dx=bx-ax,dz=bz-az,steps=Math.ceil(Math.hypot(dx,dz)/.95);
      for(let i=0;i<steps;i++){
        const t=(i+.5)/steps;
        const tile=CreateGround('inferno-worn-paving',{width:1.6,height:1.08},scene);
        tile.position.set(ax+dx*t,.018,az+dz*t);
        tile.rotation.y=Math.atan2(dx,dz)+Math.sin(stones.length*4.3)*.045;
        tile.material=paving;tile.isPickable=false;stones.push(tile);
      }
    }
    const path=Mesh.MergeMeshes(stones,true,true);
    if(path){path.isPickable=false;path.receiveShadows=true;path.freezeWorldMatrix();this.path=path;this.pathBase=Array.from(path.getVerticesData('position')??[]);}
    const haze=this.haze();
    for(let i=0;i<3;i++){
      const mesh=CreatePlane('acheron-low-mist',{width:17,height:1.6},scene);
      mesh.position.set(-8+i*8.5,-.2,14.1+i*.8);mesh.billboardMode=Mesh.BILLBOARDMODE_Y;
      mesh.material=haze;mesh.isPickable=false;this.mist.push(mesh);
    }
  }

  /** Nearby souls recoil briefly from a destruction, rather than acting as fighters. */
  updateTerrain(states:Iterable<[string,TerrainDamage]>):void {
    const holes=[...states].filter(([,s])=>s.depth>0),key=holes.map(([id])=>id).join('|');
    if(!this.path || key===this.pathKey)return;this.pathKey=key;
    const boxes=holes.map(([id])=>{const [ix,iz]=id.split(':').map(Number);return {x:TERRAIN.minX+ix!*2,z:TERRAIN.minZ+iz!*2};});
    const p=[...this.pathBase];
    for(let i=0;i<p.length;i+=12){
      const xs=[p[i]!,p[i+3]!,p[i+6]!,p[i+9]!],zs=[p[i+2]!,p[i+5]!,p[i+8]!,p[i+11]!];
      const gone=boxes.some(b=>Math.max(...xs)>b.x && Math.min(...xs)<b.x+2 && Math.max(...zs)>b.z && Math.min(...zs)<b.z+2);
      if(gone)for(let j=0;j<4;j++)p[i+j*3+1]=-3.05;
    }
    this.path.setVerticesData('position',p,true);
  }

  react(x:number,z:number):void {
    for(const soul of this.souls)if(Math.hypot(soul.x-x,soul.z-z)<12)soul.fright=1;
  }

  update(dt:number):void {
    this.time+=dt;
    for(const soul of this.souls){
      soul.fright=Math.max(0,soul.fright-dt*.55);
      soul.mesh.position.set(soul.x+Math.sin(this.time*.35+soul.phase)*.16+soul.fright*.35,
        soul.y+Math.sin(this.time*1.2+soul.phase)*.08,soul.z+soul.fright*.65);
      soul.mesh.rotation.z=Math.sin(this.time*.65+soul.phase)*.025+soul.fright*.1;
    }
    for(const {mesh,phase}of this.lanterns){
      const light=.7+.08*Math.sin(this.time*6+phase)+.025*Math.sin(this.time*17);
      (mesh.material as StandardMaterial).emissiveColor.set(light,light*.88,light*.7);
    }
    for(let i=0;i<this.mist.length;i++)this.mist[i]!.position.x=-8+i*8.5+Math.sin(this.time*.12+i)*1.4;
  }

  private sprite(kind:string,x:number,y:number,z:number,height:number,alpha=1):Mesh {
    let texture=this.textures.get(kind);
    if(!texture){
      texture=new Texture(`/proto2d/inferno-rich/${kind}-0.png`,this.scene,true,true,Texture.NEAREST_SAMPLINGMODE);
      texture.hasAlpha=true;texture.wrapU=texture.wrapV=Texture.CLAMP_ADDRESSMODE;this.textures.set(kind,texture);
    }
    const material=new StandardMaterial(`inferno-detail-${kind}`,this.scene);
    material.diffuseTexture=texture;material.useAlphaFromDiffuseTexture=true;material.backFaceCulling=false;
    material.specularColor=Color3.Black();material.emissiveColor=new Color3(.65,.59,.52);material.alpha=alpha;
    const mesh=CreatePlane(`inferno-detail-${kind}`,{width:height,height},this.scene);
    mesh.billboardMode=Mesh.BILLBOARDMODE_Y;mesh.position.set(x,y+height/2,z);
    mesh.material=material;mesh.isPickable=false;return mesh;
  }

  private paving():StandardMaterial {
    const texture=new DynamicTexture('worn-paving',{width:64,height:64},this.scene,false,Texture.NEAREST_SAMPLINGMODE);
    const g=texture.getContext() as unknown as CanvasRenderingContext2D;
    g.clearRect(0,0,64,64);
    for(let row=0;row<3;row++)for(let col=0;col<2;col++){
      const x=col*30+(row%2)*3+2,y=row*20+2;
      g.fillStyle=['#514239','#5d4e43','#473b34'][(row+col)%3]!;
      g.fillRect(x,y,26,16);g.fillStyle='#76614a';g.fillRect(x+1,y+1,23,2);
      g.fillStyle='#302923';g.fillRect(x+4,y+12,19,3);
      g.clearRect(x+col*8,y+8,6,8);g.clearRect(x+21,y,5,4);
    }
    texture.hasAlpha=true;texture.update();
    const material=new StandardMaterial('worn-paving',this.scene);
    material.diffuseTexture=texture;material.useAlphaFromDiffuseTexture=true;material.specularColor=Color3.Black();
    material.emissiveColor=new Color3(.13,.1,.07);return material;
  }

  private haze():StandardMaterial {
    const texture=new DynamicTexture('river-mist',{width:128,height:32},this.scene,false,Texture.BILINEAR_SAMPLINGMODE);
    const g=texture.getContext() as unknown as CanvasRenderingContext2D;
    g.clearRect(0,0,128,32);
    const gradient=g.createRadialGradient(64,16,0,64,16,64);
    gradient.addColorStop(0,'rgba(165,141,124,.13)');gradient.addColorStop(1,'rgba(165,141,124,0)');
    g.fillStyle=gradient;g.fillRect(0,0,128,32);texture.hasAlpha=true;texture.update();
    const material=new StandardMaterial('river-mist',this.scene);
    material.diffuseTexture=texture;material.useAlphaFromDiffuseTexture=true;material.disableLighting=true;
    material.emissiveColor=new Color3(.6,.52,.45);material.backFaceCulling=false;material.alpha=.5;return material;
  }
}
